import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PageTitle } from '../../../components/common/PageTitle/PageTitle';
import * as S from '../Legal.styles';
import { BREAKPOINTS } from '../../../styles/themes/constants';

const href = window.location.href;
const noIndex = true;

// Responsive table wrapper
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 24px 0 32px 0;
  
  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
    
    th, td {
      text-align: left;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      
      @media (max-width: ${BREAKPOINTS.md}px) {
        padding: 10px 12px;
        font-size: 13px;
      }
    }
    
    th {
      background: #fafafa;
      font-weight: 600;
      color: #1a1a2e;
    }
    
    tr:hover td {
      background: #f0f5ff;
    }
    
    a {
      color: #1677ff;
      text-decoration: none;
      word-break: break-all;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const SubprocessorsPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <PageTitle 
        title={t('head:aboutUsTitle')} 
        description={t('head:aboutUsDescription')} 
        url={href} 
        noIndex={noIndex} 
      />
      <S.H1 level={1}>Subprocessors</S.H1>
      <S.ContentRight>Last Updated: 30 June 2024</S.ContentRight>

      <S.Paragraph>To support delivery of our Services, OMR Quiz may engage and use data processors with access to certain Customer Data (each, a “
        <S.Span>Subprocessor</S.Span>. This page provides important information about the identity, location and role of each Subprocessor.</S.Paragraph>

      <S.H2 level={2}>Third Parties</S.H2>
      <S.Paragraph>OMR Quiz uses third party Subprocessors to provide infrastructure services. Prior to engaging any third party Subprocessor we perform diligence to evaluate their privacy, security and confidentiality practices.</S.Paragraph>

      <S.H5 level={2}>Infrastructure Subprocessors</S.H5>
      <S.Paragraph>OMR Quiz uses the following Subprocessors to host Customer Data or provide other infrastructure that helps with delivery of our Services:</S.Paragraph>

      <TableWrapper>
        <table>
          <thead>
            <tr>
              <th>Entity Name</th>
              <th>Subprocessing Activities</th>
              <th>Entity Country</th>
              <th>Website</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Amazon Web Services, Inc.</td>
              <td>Cloud Service Provider</td>
              <td>United States</td>
              <td><a href="https://aws.amazon.com/" rel="alternate">aws.amazon.com</a></td>
            </tr>
            <tr>
              <td>Google LLC</td>
              <td>Website Traffic Analytics Provider</td>
              <td>United States</td>
              <td><a href="https://analytics.google.com" rel="alternate">analytics.google.com</a></td>
            </tr>
            <tr>
              <td>PayPal Inc</td>
              <td>Payment Gateway Provider</td>
              <td>United States</td>
              <td><a href="https://www.paypal.com/" rel="alternate">paypal.com</a></td>
            </tr>
            <tr>
              <td>Amazon Route 53 (AWS)</td>
              <td>DNS and Content Distribution</td>
              <td>United States</td>
              <td><a href="https://aws.amazon.com/route53/" rel="alternate">aws.amazon.com/route53</a></td>
            </tr>
          </tbody>
        </table>
      </TableWrapper>
      
      <S.Section>
        <S.H2 level={2}>Updates</S.H2>
        <S.Paragraph>
          As our business grows and evolves, the Subprocessors we engage may also change. For our update policy check the{' '}
          <S.TextLink to={{ pathname: '/legal/tos' }}>Terms of Service</S.TextLink>.
          We will post any such updates here, please check back frequently for updates.
        </S.Paragraph>
      </S.Section>
    </>
  );
};

export default SubprocessorsPage;